/**
 * Image file helpers shared by every upload surface (feed, comments, DMs,
 * avatars, training covers).
 *
 * HEIC/HEIF (iPhone photos on a Mac or Windows desktop) is the reason this
 * exists: on Windows the browser reports no MIME type for .heic files, so a
 * plain `file.type.startsWith('image/')` check silently drops them and an
 * `accept="image/*"` picker may not even list them. The backend converts
 * HEIC to JPEG on upload, so the client only has to let the file through.
 */

const HEIC_EXT_RE = /\.(heic|heif|hif|heics|heifs)$/i;
const HEIC_TYPES = new Set(['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence']);

/** Picker `accept` value: every image type plus HEIC by extension for Windows. */
export const IMAGE_ACCEPT = 'image/*,.heic,.heif';

/** True for any image the backend accepts, including HEIC with an empty type. */
export function isImageFile(file) {
  if (!file) return false;
  const type = file.type || '';
  if (type.startsWith('image/')) return true;
  return HEIC_EXT_RE.test(file.name || '');
}

/**
 * HEIC/HEIF by declared type or name. Chrome/Edge/Firefox cannot draw it
 * (Safari can), so callers that build a data-URL preview for a component that
 * caches what it is given (avatars) skip the preview for these; thumbnails
 * elsewhere use LocalImageThumb, which falls back on decode failure instead.
 */
export function isHeicFile(file) {
  if (!file) return false;
  return HEIC_TYPES.has((file.type || '').toLowerCase()) || HEIC_EXT_RE.test(file.name || '');
}
